import { Alert, Paper, Snackbar } from "@mui/material";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import Grid from "@mui/material/Grid2";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import { setIn } from "final-form";
import { Autocomplete, Checkboxes, Select, TextField } from "mui-rff";
import React, { useEffect, useState } from "react";
import { Form } from "react-final-form";
import { CreateUserValidationSchema } from "utilities/form-validation/CreateUserValidationSchema";
import {
  AccountDto,
  AddUserRequestDto,
  OfficeDto,
  UpdateUserRequestDto,
  UserDto,
  UserRoleDto,
} from "../../../api/PricingPlatformApi.generated";
import Close from "../../../icons/Close";
import {
  CreateUserFormValues,
  InitialCreateUserFormValues,
} from "../../../utilities/form-initial-values/AddUserInitialValues";
import { useApiContext } from "contexts/ApiProvider";

interface AddNewUserDrawerProps {
  state: boolean;
  setState: any;
  newUserAdded?: any;
  toggleDrawer: any;
  user?: UserDto | null;
}

const AddNewUserDrawer = (props: AddNewUserDrawerProps) => {
  const { state, setState, newUserAdded, toggleDrawer, user } = props;
  const editMode = user !== null && user !== undefined;
  const editUserFormValues: CreateUserFormValues = {
    firstName: user?.firstName ?? "",
    lastName: user?.lastName ?? "",
    email: user?.email ?? "",
    isAdmin: user?.isAdmin ?? false,
    officeId: user?.office?.id?.toString() ?? "",
    userRoleId: user?.userRoleId?.toString() ?? "",
    accountId: user?.account?.id?.toString() ?? "",
    id: user?.id ?? null,
  };
  const [showToast, setShowToast] = React.useState(false);
  const [showEmailExistsMsg, setShowEmailExistsMsg] = React.useState(false);

  const [userRoles, setUserRoles] = useState<UserRoleDto[] | undefined>([]);
  const [offices, setOffices] = useState<OfficeDto[]>([]);
  const [accounts, setAccounts] = useState<AccountDto[]>([]);

  const { userApi, accountApi, officeApi } = useApiContext();

  useEffect(() => {
    const getUserRoles = async () => {
      const data = await userApi.getUserRoles();
      // filtering out api user, as we have new UI flow for creating API user
      const filteredAPIUser = data.filter((role) => role.id !== 5);
      setUserRoles(filteredAPIUser);
    };
    const getOffices = async () => {
      const data = await officeApi.getOfficeList();
      setOffices(data);
    };

    getUserRoles();
    getOffices();
  }, []);
  const submitUser = async (values: CreateUserFormValues) => {
    if (editMode) {
      callUpdateUserApi(values);
    } else {
      setShowEmailExistsMsg(false);
      userApi
        .getUserByEmail(values.email)
        .then((response) => {
          if (response) {
            setShowEmailExistsMsg(true);
          }
        })
        .catch(() => {
          callCreateUserApi(values);
        });
    }
  };
  const callCreateUserApi = async (values: CreateUserFormValues) => {
    const addUserRequestDto = AddUserRequestDto.fromJS(values);
    if (!addUserRequestDto.officeId) addUserRequestDto.officeId = undefined;

    userApi
      .create(addUserRequestDto)
      .then((response) => {
        setShowToast(true);
        newUserAdded(response);
        setState(false);
      })
      .catch((error) => {
        console.log(error);
      });
  };
  const callUpdateUserApi = async (values: CreateUserFormValues) => {
    const updateUserRequestDto = UpdateUserRequestDto.fromJS(values);
    if (!updateUserRequestDto.officeId)
      updateUserRequestDto.officeId = undefined;
    userApi
      .update(updateUserRequestDto)
      .then((response) => {
        setShowToast(true);
        newUserAdded(response);
        setState(false);
      })
      .catch((error) => {
        console.log(error);
      });
  };
  const validateFormValues =
    (schema: any) => async (values: CreateUserFormValues) => {
      if (typeof schema === "function") {
        schema = schema();
      }
      try {
        await schema.validate(values, { abortEarly: false });
      } catch (err: any) {
        const errors = err.inner.reduce((formError: any, innerError: any) => {
          return setIn(formError, innerError.path, innerError.message);
        }, {});
        return errors;
      }
    };
  const validate = validateFormValues(CreateUserValidationSchema);

  return (
    <div>
      <React.Fragment key="right">
        <Drawer
          anchor={"right"}
          open={state}
          onClose={toggleDrawer(false)}
          PaperProps={{
            sx: {
              backgroundColor: "#DAE8EE",
            },
          }}
        >
          <Grid container mt={9} direction="column">
            <Grid container justifyContent="flex-end">
              <Button onClick={toggleDrawer(false)} color="secondary">
                <Close />
              </Button>
            </Grid>
            <Grid ml={2}>
              <Typography color="secondary" variant="h6">
                {editMode ? "Update User" : "Add New User"}
              </Typography>
            </Grid>
            <Grid>
              <Paper
                elevation={10}
                sx={{
                  margin: "10px 10px 10px 10px",
                  width: "350px",
                  minHeight: "calc(100vh - 160px)",
                }}
              >
                <Grid container direction="column">
                  <Form
                    initialValues={
                      editMode
                        ? editUserFormValues
                        : InitialCreateUserFormValues
                    }
                    onSubmit={(values: CreateUserFormValues) => {
                      submitUser(values);
                    }}
                    validate={validate}
                    render={({ handleSubmit, form, values }) => (
                      <>
                        <form onSubmit={handleSubmit}>
                          <Grid
                            container
                            direction="column"
                            spacing={1}
                            pl={1}
                            pt={1}
                          >
                            {showEmailExistsMsg ? (
                              <Grid>
                                <Alert severity="error" sx={{ width: "100%" }}>
                                  Email is already in use.
                                </Alert>
                              </Grid>
                            ) : (
                              ""
                            )}
                            <Grid>
                              <Typography variant="h6" mb={1}>
                                Name{" "}
                              </Typography>
                              <TextField
                                id="firstName"
                                value={values.firstName}
                                label="First Name"
                                required={true}
                                variant="outlined"
                                disabled={editMode}
                                name="firstName"
                                sx={{ width: "75%" }}
                              />
                            </Grid>
                            <Grid>
                              <TextField
                                id="lastName"
                                value={values.lastName}
                                label="Last Name"
                                disabled={editMode}
                                required={true}
                                variant="outlined"
                                name="lastName"
                                sx={{ width: "75%" }}
                              />
                            </Grid>
                            <Grid>
                              <Typography variant="h6" mb={1}>
                                Email{" "}
                              </Typography>
                              <TextField
                                id="email"
                                value={values.email}
                                label="Email"
                                disabled={editMode}
                                required={true}
                                variant="outlined"
                                name="email"
                                sx={{ width: "75%" }}
                              />
                            </Grid>
                            <Grid>
                              <Typography variant="h6" mb={1}>
                                Role{" "}
                              </Typography>
                              <Select
                                name="userRoleId"
                                id="userRole"
                                required={true}
                                value={values.userRoleId}
                                label="Role"
                                sx={{ width: "75%" }}
                              >
                                {(userRoles ?? []).map((role: UserRoleDto) => (
                                  <MenuItem value={role?.id} key={role?.id}>
                                    {role?.roleName}
                                  </MenuItem>
                                ))}
                              </Select>
                            </Grid>
                            {[2, 3, 4].includes(
                              parseInt(values?.userRoleId)
                            ) && (
                              <Grid>
                                <Checkboxes
                                  id="isAdmin"
                                  name="isAdmin"
                                  data={{ label: "Admin", value: "isAdmin" }}
                                />
                              </Grid>
                            )}
                            {[3, 4].includes(parseInt(values?.userRoleId)) && (
                              <Grid>
                                <Typography variant="h6" mb={1}>
                                  Office{" "}
                                </Typography>

                                <Autocomplete
                                  id="officeId"
                                  options={offices}
                                  freeSolo={false}
                                  getOptionLabel={(option) => "" + option.name}
                                  name="officeId"
                                  value={user?.office}
                                  sx={{ width: "75%" }}
                                  getOptionValue={(option) => option.id}
                                  isOptionEqualToValue={(option, value) => {
                                    if (value.id === undefined) return true;
                                    return option.id === value.id;
                                  }}
                                />
                              </Grid>
                            )}

                            <Grid>
                              <Typography variant="h6">
                                {user?.apiKey ? "APIKey" : ""}
                              </Typography>
                              <Typography color="secondary">
                                {" "}
                                {editMode ? user?.apiKey : ""}{" "}
                              </Typography>
                            </Grid>
                            <Grid
                              container
                              justifyContent="flex-end"
                              mt={10}
                              pr={2}
                            >
                              <Button
                                disabled={form.getState().invalid}
                                type="submit"
                                variant="contained"
                              >
                                {editMode ? "Update User" : "Create User"}
                              </Button>
                            </Grid>
                          </Grid>
                        </form>
                      </>
                    )}
                  />
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Drawer>
        <Snackbar
          open={showToast}
          autoHideDuration={4000}
          onClose={() => setShowToast(false)}
        >
          <Alert severity="success" sx={{ width: "100%" }}>
            {editMode
              ? "User updated successfully"
              : "User created successfully"}
          </Alert>
        </Snackbar>
      </React.Fragment>
    </div>
  );
};

export default AddNewUserDrawer;
